import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { AutoComplete } from '@spark';

import { data_from_server_post } from '@/shared/http/client';

/** Debounce for re-listing deployments while the endpoint / key are typed. */
const FETCH_DEBOUNCE_MS = 600;

export const parseBatchDeployments = (value) =>
    (value || '')
        .split(',')
        .map((name) => name.trim())
        .filter(Boolean);

const emptyTextFor = (canFetch, listingError, options) => {
    if (!canFetch) {
        return 'Enter the endpoint and API key above to list deployments, or type a name to add it.';
    }
    if (listingError) {
        return 'Could not list deployments - type a name to add it manually.';
    }
    if (options === null) {
        return 'Loading deployments…';
    }
    return 'No matching deployments - type a name to add it.';
};

/**
 * Searchable multi-select of the resource's deployments for the optional
 * Global-Batch override (Spark AutoComplete: typing filters, ArrowUp/Down
 * navigate, Enter selects, tags remove on click).
 *
 * Options come from ``/list_azure_openai_deployments`` using the — possibly
 * unsaved — endpoint + key currently in the form. Deployments the backend
 * already auto-detected as Global-Batch (ARM SKU discovery) are marked
 * "(auto-detected)": selecting them is redundant but harmless. When the
 * listing is unavailable the field degrades to free-text entry through the
 * trailing `Add "<name>"` row.
 */
const AzureBatchDeploymentsPicker = ({ value, onChange, endpoint, apiKey, disabled, inputRef }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [options, setOptions] = useState(null); // null = not loaded yet
    const [listingError, setListingError] = useState(null);

    const selected = parseBatchDeployments(value);
    const canFetch = Boolean((endpoint || '').trim() && (apiKey || '').trim());

    useEffect(() => {
        if (!canFetch) {
            setOptions(null);
            setListingError(null);
            return undefined;
        }
        const timer = setTimeout(() => {
            data_from_server_post(
                'list_azure_openai_deployments',
                { azure_api_base: endpoint.trim(), azure_api_key: apiKey.trim() },
                (result) => {
                    setOptions(result?.deployments || []);
                    setListingError(result?.error || null);
                },
                (error) => {
                    setOptions([]);
                    setListingError(String(error));
                },
            );
        }, FETCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [canFetch, endpoint, apiKey]);

    const commit = (names) => onChange(names.join(', '));

    const toggle = (name) => {
        if (!name) return;
        commit(selected.includes(name) ? selected.filter((n) => n !== name) : [...selected, name]);
    };

    const loaded = options || [];
    const items = loaded.map((deployment) => ({
        label: deployment.batch_detected ? `${deployment.id} (auto-detected)` : deployment.id,
        checked: selected.includes(deployment.id),
        onClick: () => toggle(deployment.id),
    }));
    const typed = query.trim();
    const knownNames = new Set(
        [...loaded.map((deployment) => deployment.id), ...selected].map((name) => name.toLowerCase()),
    );
    if (typed && !knownNames.has(typed.toLowerCase())) {
        // Free-text escape hatch: the listing can be unavailable (no
        // credentials yet / ARM unreachable) or incomplete.
        items.push({ label: `Add "${typed}"`, onClick: () => toggle(typed) });
    }

    const handleOpenChange = (next) => {
        setOpen(next);
        if (!next) setQuery('');
    };

    return (
        <div
            className="settings-batch-picker"
            onClick={disabled ? undefined : () => setOpen(true)}
            onFocus={disabled ? undefined : () => setOpen(true)}
        >
            <AutoComplete
                type="multi-select"
                size="medium"
                placeholder={selected.length ? 'Search deployments' : 'e.g. gpt-4o-batch, o3-batch'}
                open={open}
                onOpenChange={handleOpenChange}
                value={query}
                tags={selected}
                disabled={disabled}
                items={items}
                emptyText={emptyTextFor(canFetch, listingError, options)}
                inputRef={inputRef}
                onChange={(event) => setQuery(event.target.value)}
                onTagRemove={(tag) => commit(selected.filter((name) => name !== tag))}
            />
        </div>
    );
};

AzureBatchDeploymentsPicker.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    endpoint: PropTypes.string,
    apiKey: PropTypes.string,
    disabled: PropTypes.bool,
    inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
};

export default AzureBatchDeploymentsPicker;
